import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, Plus, ArrowLeft, Store } from "lucide-react";
import { toast } from "sonner";
import { getMaisonHome, getRestaurantMenu } from "../lib/api";
import { chf } from "../lib/format";
import { useCartStore } from "../store/cartStore";

const GREEN = "#075A43";
const GREEN_DARK = "#004A36";
const ORANGE = "#FF5A1F";

// Page détail du catalogue RESTAURANT La BASE Maison — 100 % pilotée par le Board Founder
// (rubriques + produits lus en base via /maison/home). Aucun Shopify, aucune rubrique codée en dur.
export default function MaisonMenu() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [home, setHome] = useState(null);
  const [loading, setLoading] = useState(true);
  const addItem = useCartStore((s) => s.addItem);

  const rid = params.get("rid") || "";

  useEffect(() => {
    setLoading(true);
    const fetcher = rid
      ? getRestaurantMenu(rid).then((d) => ({
          rubriques: (d.categories || []).map((c) => ({
            id: c.id, name: c.name, image_url: c.image_url, items: c.items || [],
          })),
        }))
      : getMaisonHome();
    fetcher.then(setHome).catch(() => setHome({ rubriques: [] })).finally(() => setLoading(false));
  }, [rid]);

  const rubriques = home?.rubriques || [];
  const cat = params.get("category") || rubriques[0]?.id || "";
  const setCat = (key) => setParams(rid ? { rid, category: key } : { category: key });
  const current = rubriques.find((r) => r.id === cat) || rubriques[0] || null;

  const products = ((current?.items) || []).map((it) => ({
    id: it.id, title: it.name, image_url: it.image_url,
    price: it.effective_price != null ? it.effective_price : it.price,
    original_price: it.original_price != null ? it.original_price : it.price,
    promo_active: !!it.promo_active, promo_label: it.promo_label,
    description: it.description || it.subtitle || "",
  }));

  const add = (p, e) => {
    e.stopPropagation();
    addItem({ id: p.id, title: p.title, price: p.price, image_url: p.image_url, collectionKeywords: cat });
    toast.success(`${(p.title || "").slice(0, 40)} ajouté au panier`);
  };

  const openDetail = (p) => nav(`/produit/${encodeURIComponent(p.id)}`, { state: { product: p, handle: cat } });

  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="maison-menu-page">
      <button onClick={() => nav("/restaurant-boissons")} className="flex items-center gap-1 mb-5 font-semibold" style={{ color: "#101714" }} data-testid="mm-back">
        <ArrowLeft size={20} /> Restaurant &amp; Boissons
      </button>

      <div className="flex items-center gap-2 rounded-2xl px-4 h-12 mb-6" style={{ background: "#FFFFFF", border: "1px solid #E9ECEA", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
        <Search size={18} color="#68736F" />
        <input data-testid="mm-search" disabled placeholder="Rechercher un plat..." className="flex-1 bg-transparent outline-none text-sm" />
      </div>

      {rubriques.length > 0 && (
        <div className="flex gap-4 overflow-x-auto pb-2 mb-6" data-testid="mm-cats">
          {rubriques.map((c) => {
            const active = c.id === cat;
            return (
              <button key={c.id} data-testid={`mm-cat-${c.id}`} onClick={() => setCat(c.id)} className="flex flex-col items-center gap-2 flex-shrink-0" style={{ width: 74 }}>
                <div className="rounded-2xl overflow-hidden flex items-center justify-center" style={{ width: 64, height: 64, background: c.image_url ? "#F5F5F2" : GREEN_DARK, border: active ? `3px solid ${GREEN_DARK}` : "3px solid transparent", boxShadow: "0 2px 8px rgba(0,0,0,0.10)" }}>
                  {c.image_url ? <img src={c.image_url} alt={c.name} className="w-full h-full object-cover" /> : <Store size={22} color="#fff" />}
                </div>
                <span className="text-xs font-bold text-center" style={{ color: active ? GREEN_DARK : "#68736F" }}>{c.name}</span>
              </button>
            );
          })}
        </div>
      )}

      <h1 className="text-2xl md:text-3xl font-extrabold" style={{ color: GREEN }}>{current?.name || "La BASE Maison"}</h1>
      <p className="text-sm mt-1 mb-5" style={{ color: "#68736F" }}>Nos créations maison, préparées chez nous.</p>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="mm-loading">
          {[...Array(8)].map((_, i) => <div key={i} className="h-64 rounded-2xl animate-pulse" style={{ background: "#E9ECEA" }} />)}
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-16" data-testid="mm-empty" style={{ color: "#68736F" }}>Aucun produit dans cette rubrique pour le moment.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {products.map((p) => (
            <div key={p.id} data-testid={`mm-item-${p.id}`} onClick={() => openDetail(p)}
              className="rounded-2xl overflow-hidden bg-white cursor-pointer flex flex-col transition-transform hover:-translate-y-1" style={{ border: "1px solid #EFEFEC" }}>
              <div className="relative aspect-square" style={{ background: "#F5F5F2" }}>
                {p.image_url && <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" />}
                <button onClick={(e) => add(p, e)} data-testid={`mm-add-${p.id}`}
                  className="absolute right-2 bottom-2 w-9 h-9 rounded-full flex items-center justify-center text-white" style={{ background: GREEN_DARK, boxShadow: "0 2px 6px rgba(0,0,0,0.25)" }}>
                  <Plus size={18} />
                </button>
              </div>
              <div className="p-3 flex-1 flex flex-col">
                <p className="text-sm font-bold line-clamp-2" style={{ color: "#101714" }}>{p.title}</p>
                <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                  {p.promo_active && p.original_price > p.price && (
                    <span className="text-xs line-through" style={{ color: "#9aa0a6" }}>{chf(p.original_price)}</span>
                  )}
                  {p.price != null && <span className="font-extrabold" style={{ color: ORANGE }}>{chf(p.price)}</span>}
                  {p.promo_active && p.promo_label && (
                    <span data-testid={`mm-promo-${p.id}`} className="text-[10px] font-extrabold px-1.5 py-0.5 rounded-full text-white" style={{ background: ORANGE }}>{p.promo_label}</span>
                  )}
                </div>
                {p.description && <p className="text-xs mt-1 line-clamp-2" style={{ color: "#68736F" }}>{String(p.description).replace(/<[^>]+>/g, "")}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
